import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'

export type OnboardingStep =
  | 'welcome'
  | 'integrations'
  | 'availability'
  | 'packages'
  | 'contracts'
  | 'review'
  | 'profile'
  | 'services'

export type OnboardingFlow = 'studio' | 'professional'

export const STUDIO_ONBOARDING_STEPS: OnboardingStep[] = [
  'welcome',
  'integrations',
  'availability',
  'packages',
  'contracts',
  'review',
]

export const PROFESSIONAL_ONBOARDING_STEPS: OnboardingStep[] = [
  'welcome',
  'profile',
  'services',
  'availability',
  'review',
]

export const ONBOARDING_STEPS = STUDIO_ONBOARDING_STEPS

export function getStepsForFlow(flow: OnboardingFlow): OnboardingStep[] {
  return flow === 'professional' ? PROFESSIONAL_ONBOARDING_STEPS : STUDIO_ONBOARDING_STEPS
}

type OnboardingStatus = {
  flow: OnboardingFlow
  role: string
  tenantName: string | null
  hasCompletedOnboarding: boolean
  onboardingStep: number
  onboardingCompletedAt: string | null
  integrations: {
    stripe: boolean
    calendar: boolean
  }
  availability: {
    configured: boolean
    timeZone: string | null
    activeWindowsCount: number
  }
  hasPackages: boolean
  hasContractTemplate: boolean
  professional: {
    hasProfile: boolean
    displayName: string | null
    publicationStatus: string | null
    assignmentsCount: number
    availabilityWindowsCount: number
  } | null
}

type OnboardingContextType = {
  isLoading: boolean
  isOpen: boolean
  flow: OnboardingFlow
  steps: OnboardingStep[]
  currentStep: OnboardingStep
  currentStepIndex: number
  status: OnboardingStatus | null
  openOnboarding: () => void
  closeOnboarding: () => void
  goToStep: (step: OnboardingStep) => void
  nextStep: () => void
  prevStep: () => void
  completeOnboarding: () => Promise<void>
  skipOnboarding: () => Promise<void>
  refreshStatus: () => Promise<void>
  canProceed: (step: OnboardingStep) => boolean
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

const ONBOARDING_LOCAL_KEY = 'pinday_onboarding_progress'

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [status, setStatus] = useState<OnboardingStatus | null>(null)
  const hasFetchedRef = useRef(false)

  const flow: OnboardingFlow = status?.flow ?? 'studio'
  const steps = getStepsForFlow(flow)
  const currentStep = steps[Math.max(0, Math.min(currentStepIndex, steps.length - 1))]
  const clampStepIndex = useCallback((step: number, flowSteps: OnboardingStep[] = steps) => (
    Math.max(0, Math.min(step, flowSteps.length - 1))
  ), [steps])

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated || hasFetchedRef.current) return

    hasFetchedRef.current = true

    try {
      setIsLoading(true)
      const data = await api.getOnboardingStatus()
      setStatus(data)

      // Auto-open onboarding if not completed
      if (!data.hasCompletedOnboarding) {
        setIsOpen(true)
        setCurrentStepIndex(clampStepIndex(data.onboardingStep, getStepsForFlow(data.flow ?? 'studio')))
      }

      // Save to localStorage as fallback
      localStorage.setItem(ONBOARDING_LOCAL_KEY, JSON.stringify({
        step: data.onboardingStep,
        completed: data.hasCompletedOnboarding,
      }))
    } catch (error) {
      console.error('Failed to fetch onboarding status:', error)
      hasFetchedRef.current = false // Allow retry on error
      
      // Try to recover from localStorage
      const cached = localStorage.getItem(ONBOARDING_LOCAL_KEY)
      if (cached) {
        try {
          const parsed = JSON.parse(cached)
          if (!parsed.completed) {
            setIsOpen(true)
            setCurrentStepIndex(clampStepIndex(parsed.step || 0))
          }
        } catch {
          // Ignore parse errors
        }
      }
    } finally {
      setIsLoading(false)
    }
  }, [clampStepIndex, isAuthenticated])

  useEffect(() => {
    if (!authLoading && isAuthenticated && !hasFetchedRef.current) {
      fetchStatus()
    } else if (!authLoading && !isAuthenticated) {
      setIsLoading(false)
      hasFetchedRef.current = false // Reset when logged out
    }
  }, [authLoading, isAuthenticated, fetchStatus])

  const openOnboarding = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeOnboarding = useCallback(() => {
    setIsOpen(false)
  }, [])

  const goToStep = useCallback(async (step: OnboardingStep) => {
    const index = steps.indexOf(step)
    if (index !== -1) {
      setCurrentStepIndex(index)
      
      try {
        await api.updateOnboardingStep(index)
        localStorage.setItem(ONBOARDING_LOCAL_KEY, JSON.stringify({
          step: index,
          completed: false,
        }))
      } catch (error) {
        console.error('Failed to update onboarding step:', error)
      }
    }
  }, [steps])

  const nextStep = useCallback(async () => {
    const nextIndex = Math.min(currentStepIndex + 1, steps.length - 1)
    setCurrentStepIndex(nextIndex)
    
    try {
      await api.updateOnboardingStep(nextIndex)
      localStorage.setItem(ONBOARDING_LOCAL_KEY, JSON.stringify({
        step: nextIndex,
        completed: false,
      }))
    } catch (error) {
      console.error('Failed to update onboarding step:', error)
    }
  }, [currentStepIndex, steps.length])

  const prevStep = useCallback(async () => {
    const prevIndex = Math.max(currentStepIndex - 1, 0)
    setCurrentStepIndex(prevIndex)
    
    try {
      await api.updateOnboardingStep(prevIndex)
      localStorage.setItem(ONBOARDING_LOCAL_KEY, JSON.stringify({
        step: prevIndex,
        completed: false,
      }))
    } catch (error) {
      console.error('Failed to update onboarding step:', error)
    }
  }, [currentStepIndex])

  const completeOnboarding = useCallback(async () => {
    try {
      await api.completeOnboarding(true)
      setIsOpen(false)
      setStatus(prev => prev ? {
        ...prev,
        hasCompletedOnboarding: true,
        onboardingCompletedAt: new Date().toISOString(),
      } : null)
      
      localStorage.setItem(ONBOARDING_LOCAL_KEY, JSON.stringify({
        step: steps.length - 1,
        completed: true,
      }))
    } catch (error) {
      console.error('Failed to complete onboarding:', error)
      throw error
    }
  }, [steps])

  const skipOnboarding = useCallback(async () => {
    try {
      await api.completeOnboarding(true)
      setIsOpen(false)
      setStatus(prev => prev ? {
        ...prev,
        hasCompletedOnboarding: true,
      } : null)
      
      localStorage.setItem(ONBOARDING_LOCAL_KEY, JSON.stringify({
        step: currentStepIndex,
        completed: true,
      }))
    } catch (error) {
      console.error('Failed to skip onboarding:', error)
    }
  }, [currentStepIndex])

  const refreshStatus = useCallback(async () => {
    if (!isAuthenticated) return
    
    try {
      const data = await api.getOnboardingStatus()
      setStatus(data)
    } catch (error) {
      console.error('Failed to refresh onboarding status:', error)
    }
  }, [isAuthenticated])

  const canProceed = useCallback((step: OnboardingStep): boolean => {
    if (!status) return false

    switch (step) {
      case 'welcome':
        return true
      case 'integrations':
        // Can proceed even without integrations, but show warning
        return true
      case 'packages':
        return true
      case 'contracts':
        return true
      case 'review':
        return true
      default:
        return true
    }
  }, [status])

  return (
    <OnboardingContext.Provider
      value={{
        isLoading,
        isOpen,
        flow,
        steps,
        currentStep,
        currentStepIndex,
        status,
        openOnboarding,
        closeOnboarding,
        goToStep,
        nextStep,
        prevStep,
        completeOnboarding,
        skipOnboarding,
        refreshStatus,
        canProceed,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
}
